import { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Hook to fetch a menu's color scheme early (before full menu loads)
 * This prevents white flash during loading by setting the theme immediately
 * 
 * @param menuId - The ID of the menu to fetch color scheme for
 * @returns The color scheme ('light' or 'dark'), defaulting to 'light'
 */
export const useMenuColorScheme = (menuId: string | undefined) => {
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>('light');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!menuId) {
      setColorScheme('light');
      setLoading(false);
      return;
    }

    const fetchColorScheme = async () => {
      try {
        setLoading(true);
        // Fetch only the color scheme - this is much faster than full menu
        const response = await api.get(`/menus/${menuId}/color-scheme`);
        const scheme = response.data.colorScheme || 'light';
        setColorScheme(scheme as 'light' | 'dark');
      } catch (err) {
        console.error('Error fetching menu color scheme:', err);
        // Default to light on error
        setColorScheme('light');
      } finally {
        setLoading(false);
      }
    };

    fetchColorScheme();
  }, [menuId]);

  return { colorScheme, loading };
};

